import React, { useRef, useEffect, useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/contexts/StoreContext';

import { usePersistence } from '@/hooks/usePersistence';
import { useCanvasInteraction } from '@/hooks/useCanvasInteraction';
import {
  getRelativeMousePosition,
  screenToCanvas,
  canvasCoordinatesToPoint,
} from '@/utils/geometry/coordinates';
import { ElementRenderer } from '../Elements';
import { getStrokeDashArray } from '@/utils/strokeStyles';
import styles from './Canvas.module.css';
import { getPatternConfig } from '@/utils/fillPatterns';
import { ConnectionPoints } from '../Elements/ConnectionPoints';
import { Element } from '@/types/elements';
import { findInsertionPointOnPath } from '@/utils/bezier';

export const Canvas: React.FC = observer(() => {
  const svgRef = useRef<SVGSVGElement>(null);
  const { projectStore, uiStore } = useStores();

  const { elements, selectedElementIds } = projectStore;
  const {
    activeTool,
    previewElement,
    canvasOffset,
    zoom,
    isPanning,
    knotInsertionPreview,
    selectToolMode,
  } = uiStore;

  // Hooks for side effects
  usePersistence();
  useCanvasInteraction(svgRef);

  const [hoveredElement, setHoveredElement] = useState<Element | null>(null);

  const handlePointerMoveForHover = (event: React.PointerEvent<SVGSVGElement>) => {
    if (
      hoveredElement &&
      (hoveredElement.type === 'line' || hoveredElement.type === 'arrow') &&
      hoveredElement.isSelected
    ) {
      const point = getRelativeMousePosition(
        event.nativeEvent,
        svgRef.current!
      );
      const canvasCoords = screenToCanvas(point.x, point.y, {
        canvasOffset,
        zoom,
      });
      const canvasPoint = canvasCoordinatesToPoint(canvasCoords);

      const insertionPoint = findInsertionPointOnPath(
        canvasPoint,
        hoveredElement.points
      );
      if (insertionPoint) {
        uiStore.setKnotInsertionPreview({
          ...insertionPoint,
          elementId: hoveredElement.id,
        });
      } else {
        uiStore.setKnotInsertionPreview(null);
      }
    } else if (knotInsertionPreview) {
      uiStore.setKnotInsertionPreview(null);
    }
  };

  const handleWheel = useCallback((event: WheelEvent) => {
    event.preventDefault();
    if (!svgRef.current) return;

    const point = getRelativeMousePosition(event, svgRef.current);
    const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.1, Math.min(5, zoom * zoomFactor));

    const canvasCoords = screenToCanvas(point.x, point.y, { canvasOffset, zoom });
    const zoomPoint = canvasCoordinatesToPoint(canvasCoords);
    const newOffsetX = point.x - zoomPoint.x * newZoom;
    const newOffsetY = point.y - zoomPoint.y * newZoom;

    uiStore.setZoom(newZoom);
    uiStore.setOffset({ x: newOffsetX, y: newOffsetY });
  }, [uiStore, zoom, canvasOffset]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    // Only handle drag over for icon tool, not select tool
    if (activeTool !== 'icon') {
      return;
    }
    
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, [activeTool]);

  const handleDrop = useCallback((event: React.DragEvent) => {
    // Only handle drops for icon tool, not select tool
    if (activeTool !== 'icon') {
      return;
    }
    
    event.preventDefault();
    
    if (!svgRef.current) return;

    try {
      const dataString = event.dataTransfer.getData('application/json');
      const data = JSON.parse(dataString);
      
      if (data.type === 'icon') {
        const point = getRelativeMousePosition(event.nativeEvent, svgRef.current);
        const canvasCoords = screenToCanvas(point.x, point.y, { canvasOffset, zoom });
        const canvasPoint = canvasCoordinatesToPoint(canvasCoords);

        // Create the icon element - icons never have background/fill colors
        const iconElement = {
          id: `icon-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'icon' as const,
          x: canvasPoint.x - data.width / 2, // Center the icon on drop point
          y: canvasPoint.y - data.height / 2,
          width: data.width,
          height: data.height,
          iconName: data.iconName,
          style: { 
            ...uiStore.persistentStyle,
            strokeWidth: 1.5, // Use a thinner stroke for better icon appearance
            fillColor: 'transparent', // Icons never have background colors
            fillPattern: 'solid' as const // Icons don't use fill patterns
          },
          isSelected: false,
          zIndex: Math.max(...elements.map(el => el.zIndex), 0) + 1,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };

        const createdElement = projectStore.createElement(iconElement);
        // Immediately select the newly created icon and switch to select tool
        projectStore.select(createdElement.id);
        uiStore.setActiveTool('select');
      }
    } catch (error) {
      console.error('Failed to parse drop data:', error);
    }
  }, [projectStore, uiStore, canvasOffset, zoom, elements, activeTool]);

  useEffect(() => {
    const svgElement = svgRef.current;
    if (!svgElement) return;
    
    // Set SVG element reference in store for export functionality
    uiStore.setCanvasSvgElement(svgElement);
    
    svgElement.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      svgElement.removeEventListener('wheel', handleWheel);
      uiStore.setCanvasSvgElement(null);
    };
  }, [handleWheel, uiStore]);

  const transform = `translate(${canvasOffset.x}, ${canvasOffset.y}) scale(${zoom})`;

  const fillPatterns = new Map<string, React.ReactNode>();
  elements.forEach(element => {
    const { style } = element;
    if (style.fillPattern !== 'solid' && style.fillColor !== 'transparent') {
      const patternId = `${style.fillPattern}-${style.fillColor.replace('#', '')}`;
      if (!fillPatterns.has(patternId)) {
        const config = getPatternConfig(style.fillPattern);
        if (config) {
          fillPatterns.set(patternId, (
            <pattern
              key={patternId}
              id={patternId}
              width={config.width}
              height={config.height}
              patternUnits="userSpaceOnUse"
              patternTransform={config.patternTransform}
            >
              {config.elements.map((el, index) => (
                <line
                  key={index}
                  x1={el.x1}
                  y1={el.y1}
                  x2={el.x2}
                  y2={el.y2}
                  stroke={style.fillColor}
                  strokeWidth={el.strokeWidth}
                  opacity={el.opacity}
                />
              ))}
            </pattern>
          ));
        }
      }
    }
  });

  const isDragging = selectToolMode.type === 'dragging' || 
                    selectToolMode.type === 'resizing' || 
                    selectToolMode.type === 'multi-resizing' ||
                    selectToolMode.type === 'moving-control-point';

  return (
    <div 
      className={`${styles['canvasContainer']} ${isDragging ? styles['dragging'] : ''}`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <svg
        ref={svgRef}
        className={styles['canvas']}
        width="100%"
        height="100%"
        data-tool={activeTool}
        data-pan={isPanning}
        onPointerMove={handlePointerMoveForHover}
      >
        <g transform={transform}>
          <defs>
            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#1a1a1a" strokeWidth="0.5" />
            </pattern>
            {Array.from(fillPatterns.values())}
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
          
          {elements
            .slice() // Create a shallow copy for sorting to not trigger MobX reactions
            .sort((a, b) => a.zIndex - b.zIndex)
            .map(element => (
              <g 
                key={element.id}
                onMouseEnter={() => setHoveredElement(element)}
                onMouseLeave={() => setHoveredElement(null)}
              >
                <ElementRenderer
                  element={element}
                  isSelected={selectedElementIds.has(element.id)}
                />
              </g>
            ))}
          
          {previewElement && (
            previewElement.id === 'selection-rectangle-preview' ? (
              <rect
                x={previewElement.x}
                y={previewElement.y}
                width={previewElement.width}
                height={previewElement.height}
                fill={previewElement.style.fillColor}
                stroke={previewElement.style.strokeColor}
                strokeWidth={previewElement.style.strokeWidth}
                strokeDasharray={getStrokeDashArray(
                  previewElement.style.strokeStyle,
                  previewElement.style.strokeWidth
                )}
                opacity={previewElement.style.opacity}
              />
            ) : (
              <ElementRenderer element={previewElement} isSelected={false} />
            )
          )}

          {hoveredElement && (
            <ConnectionPoints 
              element={hoveredElement}
              zoom={zoom}
              isHovered={
                (activeTool === 'line' || activeTool === 'arrow') ||
                (activeTool === 'select' && selectedElementIds.size > 0 && ['line', 'arrow'].includes(projectStore.elements.find(el => el.id === Array.from(selectedElementIds.keys())[0])?.type || ''))
              }
            />
          )}

          {/* Show connection points on all elements when moving control points */}
          {(activeTool === 'select' && selectToolMode.type === 'moving-control-point') && elements.map(element => (
            element.type !== 'line' && element.type !== 'arrow' && 
            element.id !== (selectToolMode as any).elementId ? (
              <ConnectionPoints 
                key={`connection-${element.id}`}
                element={element}
                zoom={zoom}
                isHovered={true}
              />
            ) : null
          ))}

          {knotInsertionPreview && (
            <circle
              cx={knotInsertionPreview.position.x}
              cy={knotInsertionPreview.position.y}
              r={5 / zoom}
              fill="rgba(255, 107, 53, 0.7)"
              stroke="#ffffff"
              strokeWidth={1 / zoom}
              style={{ pointerEvents: 'none' }}
            />
          )}
        </g>
      </svg>
    </div>
  );
});

// We are not exporting as default because we'll replace the original Canvas.tsx later 